import React, { useState, useEffect } from 'react';
import { 
  Megaphone, Plus, Edit3, Trash2, Power, PowerOff, 
  AlertCircle, CheckCircle, Info, AlertTriangle, 
  ExternalLink, Calendar, RefreshCw, X 
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const HotNewsAdmin = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [form, setForm] = useState({
    title: '',
    content: '',
    link: '',
    link_text: 'Learn More',
    type: 'info',
    priority: 0,
    is_active: true,
    show_on_dashboard: true,
    show_on_header: false,
    expires_at: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const res = await api.get('/admin/hot-news');
      setNews(res.data || []);
    } catch (error) {
      toast.error('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      content: '',
      link: '',
      link_text: 'Learn More',
      type: 'info',
      priority: 0,
      is_active: true,
      show_on_dashboard: true,
      show_on_header: false,
      expires_at: ''
    });
    setEditingNews(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      if (editingNews) {
        await api.put(`/admin/hot-news/${editingNews.id}`, form);
        toast.success('News updated');
      } else {
        await api.post('/admin/hot-news', form);
        toast.success('News created');
      }
      setShowModal(false);
      resetForm();
      fetchNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setForm({
      title: item.title || '',
      content: item.content || '',
      link: item.link || '',
      link_text: item.link_text || 'Learn More',
      type: item.type || 'info',
      priority: item.priority || 0,
      is_active: item.is_active !== false,
      show_on_dashboard: item.show_on_dashboard !== false,
      show_on_header: item.show_on_header || false,
      expires_at: item.expires_at || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this news?')) return;
    try {
      await api.delete(`/admin/hot-news/${id}`);
      toast.success('News deleted');
      fetchNews();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.post(`/admin/hot-news/${id}/toggle`);
      toast.success(res.data.is_active ? 'News activated' : 'News deactivated');
      fetchNews();
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      success: <CheckCircle className="w-4 h-4 text-green-400" />,
      warning: <AlertTriangle className="w-4 h-4 text-yellow-400" />,
      urgent: <AlertCircle className="w-4 h-4 text-red-400" />,
      info: <Info className="w-4 h-4 text-blue-400" />
    };
    return icons[type] || icons.info;
  };

  const getTypeColor = (type) => {
    const colors = {
      success: 'bg-green-500/20 text-green-400 border-green-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[type] || colors.info;
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="hot-news-admin">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Megaphone className="w-8 h-8 text-yellow-500" />
              Hot News / Announcements
            </h1>
            <p className="text-muted-foreground mt-1">
              Create scrolling announcements for member dashboards
            </p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add News
          </button>
        </div>

        {/* Preview */}
        {news.filter(n => n.is_active).length > 0 && (
          <div className="glass-card p-4 mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Preview (Scrolling Marquee)</h3>
            <div className="overflow-hidden relative">
              <div className="flex gap-6 animate-marquee">
                {news.filter(n => n.is_active).map(item => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border whitespace-nowrap ${getTypeColor(item.type)}`}
                  >
                    {getTypeIcon(item.type)}
                    <span className="font-medium">{item.title}</span>
                    {item.content && <span className="text-sm opacity-75">- {item.content}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* News List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : news.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Megaphone className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-bold mb-2">No Announcements Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first announcement to show on member dashboards</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium"
            >
              Create First Announcement
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {news.map(item => (
              <div
                key={item.id}
                className={`glass-card p-4 flex items-center justify-between ${!item.is_active && 'opacity-50'}`}
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className={`p-2 rounded-lg ${getTypeColor(item.type)}`}>
                    {getTypeIcon(item.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold">{item.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(item.type)}`}>
                        {item.type}
                      </span>
                      {!item.is_active && (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-500/20 text-gray-400">
                          Inactive
                        </span>
                      )}
                    </div>
                    {item.content && <p className="text-sm text-muted-foreground">{item.content}</p>}
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 flex items-center gap-1 mt-1">
                        <ExternalLink className="w-3 h-3" /> {item.link_text}
                      </a>
                    )}
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div>Priority: {item.priority}</div>
                    {item.expires_at && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Expires: {new Date(item.expires_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggle(item.id)}
                    className={`p-2 rounded-lg ${item.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}
                    title={item.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {item.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-bold">
                  {editingNews ? 'Edit Announcement' : 'New Announcement'}
                </h2>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Announcement title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Content (Optional)</label>
                  <input
                    type="text"
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Additional details"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    >
                      <option value="info">Info (Blue)</option>
                      <option value="success">Success (Green)</option>
                      <option value="warning">Warning (Yellow)</option>
                      <option value="urgent">Urgent (Red)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <input
                      type="number"
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                      placeholder="Higher = First"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Link URL (Optional)</label>
                  <input
                    type="url"
                    value={form.link}
                    onChange={(e) => setForm({ ...form, link: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Link Text</label>
                  <input
                    type="text"
                    value={form.link_text}
                    onChange={(e) => setForm({ ...form, link_text: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="Learn More"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expires At (Optional)</label>
                  <input
                    type="datetime-local"
                    value={form.expires_at ? form.expires_at.slice(0, 16) : ''}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.show_on_dashboard}
                      onChange={(e) => setForm({ ...form, show_on_dashboard: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Show on Dashboard</span>
                  </label>
                </div>
                <div className="pt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-4 py-2 bg-white/10 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium"
                  >
                    {editingNews ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <style jsx global>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 20s linear infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default HotNewsAdmin;
